import { css } from 'aphrodite';
import { Tabs } from 'radix-ui';
import { FC, MouseEventHandler, PropsWithChildren, useEffect } from 'react';
import { useLocalStorage } from '../../../../hooks/useLocalStorage';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/Tooltip';
import { LOCAL_STORAGE_ACTIVE_APP_DRAWER_KEY } from '../AppDrawer';
import { styleSheet } from './styles';

export const StaticDrawer: FC<Tabs.TabsProps> = ({ children, value, onValueChange }) => {
	return (
		<Tabs.Root value={value} className={css(styleSheet.drawer)} orientation='vertical' onValueChange={onValueChange}>
			{children}
		</Tabs.Root>
	);
};

export const Toolbar: FC<PropsWithChildren> = ({ children }) => {
	return (
		<Tabs.List className={css(styleSheet.drawerEdge)}>
			<div className={css(styleSheet.drawerToolbarContent)}>{children}</div>
		</Tabs.List>
	);
};

interface ContentProps extends PropsWithChildren {
	value: string;
	unmountOnExit?: boolean;
	backgroundColor?: string;
	onValueChange: (value: string) => void;
	pad?: boolean;
}

export const Content: FC<ContentProps> = ({ value, children, backgroundColor, pad = true }) => {
	const inlineStyle = backgroundColor ? { backgroundColor } : undefined;

	return (
		<Tabs.Content
			value={value}
			className={css(styleSheet.drawerContent, pad && styleSheet.defaultPadding)}
			style={inlineStyle}
		>
			{children}
		</Tabs.Content>
	);
};

interface StickyContentProps extends PropsWithChildren {
	value: string;
	onValueChange: (value: string) => void;
	unmountOnExit?: boolean;
	backgroundColor?: string;
}

export const StickyContent: FC<StickyContentProps> = ({ value, onValueChange, ...rest }) => {
	const [activeAppDrawerKey] = useLocalStorage({ key: LOCAL_STORAGE_ACTIVE_APP_DRAWER_KEY });
	useEffect(() => {
		if (value === activeAppDrawerKey) {
			onValueChange(value);
		}
	}, [activeAppDrawerKey, value, onValueChange]);
	return <Content pad={false} onValueChange={onValueChange} value={value} {...rest} />;
};

interface ToolbarButtonProps extends PropsWithChildren {
	tooltip?: string;
	value: string;
	onValueChange: (value: string) => void;
}

export const ToolbarButton: FC<ToolbarButtonProps> = ({ onValueChange, value, tooltip, ...rest }) => {
	const handleClick: MouseEventHandler<HTMLButtonElement> = () => {
		onValueChange(value);
	};

	if (tooltip) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<span>
						<Tabs.Trigger className={css(styleSheet.drawerButton)} onClick={handleClick} value={value} {...rest} />
					</span>
				</TooltipTrigger>
				<TooltipContent side='left'>{tooltip}</TooltipContent>
			</Tooltip>
		);
	}
	return <Tabs.Trigger className={css(styleSheet.drawerButton)} onClick={handleClick} value={value} {...rest} />;
};
